// ============================================
// Capability-based Access Control
// ============================================
// Single source of truth for "what can this user do?" — derived from
// user_roles (NOT profiles.position_code, which is cosmetic).
//
// Mirrors the Access Matrix in
// /mnt/documents/role_department_reconciliation.xlsx  (Sheet "4. Access Matrix").
//
// Consume via `useCapabilities()` (see src/hooks/useCapabilities.ts).
// New UI gates should call capability keys — NOT raw role checks — so that
// the eventual role rename (pod_leader → sales_manager, sales_agent →
// sales_rep/setter) does not require touching every screen.

import type { AppRole } from "@/lib/roles";
import { normalizeRole } from "@/lib/roles";

export type Capability =
  | "portal.home"
  | "deals.view.own.ontario"
  | "deals.submit.own.ontario"
  | "tpv.request.own.ontario"
  | "leads.view.own"
  | "leads.view.pod"
  | "leads.view.all"
  | "deals.write"
  | "meetings.book"
  | "leads.reassign.pod"
  | "leads.reassign.global"
  | "pod.pullback"
  | "dashboards.manager"
  | "dashboards.executive"
  | "payroll.manage"
  | "invoicing.manage"
  | "recruiting.manage"
  | "training.view"
  | "training.author"
  | "scripts.view"
  | "scripts.manage"
  | "telephony.call"
  | "telephony.config"
  | "users.manage"
  | "roles.assign"
  | "roles.assign.ceo"
  | "system.settings"
  | "impersonate"
  | "records.hard_delete"
  | "security.manage";

/** Matches the existing server-account isolation; mixed labels never widen it. */
export function isScopedPortalAccount(
  roles: readonly string[],
  profile?: { external_account?: boolean } | null,
): boolean {
  return profile?.external_account === true || roles.includes("external_sales_agent");
}

/** Frontend route boundary only. Every data operation still requires server scope. */
export function canAccessScopedPortalPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  return ["/", "/dashboard", "/leads", "/deals", "/deals/new", "/onboarding", "/auth", "/change-password", "/reset-password", "/external-sales"].includes(pathname)
    || /^\/onboarding\/[^/]+$/.test(pathname)
    || /^\/(?:leads|deals)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pathname);
}

const SCOPED_PORTAL_CAPABILITIES = new Set<Capability>([
  "portal.home", "leads.view.own", "deals.view.own.ontario", "deals.submit.own.ontario", "tpv.request.own.ontario",
]);

// Effective role tiers used by capability grants. Legacy names collapse to
// their proposed new-model equivalents so shipping the capability layer
// does NOT require the role rename to land first.
type EffectiveRole =
  | "ceo"
  | "super_admin"
  | "admin"
  | "sales_manager"
  | "sales_rep"
  | "setter"
  | "recruiter"
  | "accountant";

function toEffective(role: string): EffectiveRole | null {
  const r = normalizeRole(role);
  switch (r) {
    case "ceo": return "ceo";
    case "super_admin": return "super_admin";
    case "admin": return "admin";
    case "office_manager":
    case "pod_leader":
    case "sales_manager": return "sales_manager";
    case "sales_agent": return "sales_rep"; // setter split is per-user metadata (Phase 3)
    case "recruiter": return "recruiter";
    case "accountant": return "accountant";
    default: return null;
  }
}

// Base grants per effective role. `sales_rep` grants are the superset for
// sales floor users; `setter` is a subset applied only when we know a user
// is a remote appointment setter (once Phase 3 lands).
const GRANTS: Record<EffectiveRole, Set<Capability>> = {
  ceo: new Set<Capability>([
    "leads.view.own", "leads.view.pod", "leads.view.all",
    "deals.write", "meetings.book",
    "leads.reassign.pod", "leads.reassign.global", "pod.pullback",
    "dashboards.manager", "dashboards.executive",
    "payroll.manage", "invoicing.manage", "recruiting.manage",
    "training.view", "training.author",
    "scripts.view", "scripts.manage",
    "telephony.call", "telephony.config",
    "users.manage", "roles.assign", "roles.assign.ceo",
    "system.settings", "impersonate", "records.hard_delete", "security.manage",
  ]),
  super_admin: new Set<Capability>([
    "leads.view.own", "leads.view.pod", "leads.view.all",
    "deals.write", "meetings.book",
    "leads.reassign.pod", "leads.reassign.global", "pod.pullback",
    "dashboards.manager", "dashboards.executive",
    "payroll.manage", "invoicing.manage", "recruiting.manage",
    "training.view", "training.author",
    "scripts.view", "scripts.manage",
    "telephony.call", "telephony.config",
    "users.manage", "roles.assign",
    "system.settings", "impersonate", "security.manage",
  ]),
  admin: new Set<Capability>([
    "leads.view.own", "leads.view.pod", "leads.view.all",
    "deals.write", "meetings.book",
    "leads.reassign.pod", "leads.reassign.global", "pod.pullback",
    "dashboards.manager",
    "recruiting.manage",
    "training.view", "training.author",
    "scripts.view", "scripts.manage",
    "telephony.call", "telephony.config",
    "users.manage",
  ]),
  sales_manager: new Set<Capability>([
    "leads.view.own", "leads.view.pod",
    "deals.write", "meetings.book",
    "leads.reassign.pod", "pod.pullback",
    "dashboards.manager",
    "training.view", "scripts.view", "scripts.manage",
    "telephony.call",
  ]),
  sales_rep: new Set<Capability>([
    "leads.view.own",
    "deals.write", "meetings.book",
    "training.view", "scripts.view", "telephony.call",
  ]),
  setter: new Set<Capability>([
    "leads.view.own", "meetings.book",
    "training.view", "scripts.view", "telephony.call",
  ]),
  recruiter: new Set<Capability>([
    "recruiting.manage", "training.view", "telephony.call",
  ]),
  accountant: new Set<Capability>([
    "payroll.manage", "invoicing.manage", "training.view",
  ]),
};

export function capabilitiesFor(roles: readonly (AppRole | string)[], profile?: { external_account?: boolean; canada_sales_account?: boolean } | null): Set<Capability> {
  if (profile?.canada_sales_account || roles.includes("canada_sales_agent")) return new Set();
  if (isScopedPortalAccount(roles, profile)) return new Set(SCOPED_PORTAL_CAPABILITIES);
  const out = new Set<Capability>();
  for (const r of roles ?? []) {
    const eff = toEffective(r);
    if (!eff) continue;
    for (const c of GRANTS[eff]) out.add(c);
  }
  return out;
}

export function can(roles: readonly (AppRole | string)[], capability: Capability, profile?: { external_account?: boolean; canada_sales_account?: boolean } | null): boolean {
  if (profile?.canada_sales_account || roles.includes("canada_sales_agent")) return false;
  if (isScopedPortalAccount(roles, profile)) return SCOPED_PORTAL_CAPABILITIES.has(capability);
  const eff = new Set<EffectiveRole>();
  for (const r of roles ?? []) {
    const e = toEffective(r);
    if (e) eff.add(e);
  }
  for (const e of eff) {
    if (GRANTS[e].has(capability)) return true;
  }
  return false;
}
